import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";
import type { ChangelogCandidateEntry } from "@/features/changelog/api/changelog.api";
import {
  axisPercent,
  type DateWindow,
  type DeployTimeline,
  type TimelineDay,
} from "@/features/changelog/lib/changelog-templates";

/** Beyond this a day's column would be taller than the chart; the label carries the true count. */
const MAX_BLOCKS_PER_DAY = 4;

interface ChangelogDeployTimelineProps {
  timeline: DeployTimeline<ChangelogCandidateEntry>;
  /** The stretch a template picked, drawn as a band. Null when the admin ticked by hand. */
  window: DateWindow | null;
  selected: Set<string>;
  isDisabled: boolean;
  onToggleDay: (day: TimelineDay<ChangelogCandidateEntry>) => void;
}

/**
 * Deploys plotted on the days they landed, so a template stops being a guess:
 * the band shows the fortnight it grabbed and the columns show what was in it.
 * Clicking a day is the fine adjustment — the reason the chart is a control
 * rather than a picture.
 */
export function ChangelogDeployTimeline({
  timeline,
  window,
  selected,
  isDisabled,
  onToggleDay,
}: ChangelogDeployTimelineProps) {
  const { t, i18n } = useTranslation();
  const { days, axis } = timeline;

  const dayLabel = (date: Date) =>
    date.toLocaleDateString(i18n.language, { day: "numeric", month: "short" });

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className="relative h-12 w-full"
        role="group"
        aria-label={t("admin.changelog.compose.timeline.label", "Releases over time")}
      >
        {window ? (
          <span
            aria-hidden
            className="absolute inset-y-0 rounded-sm border-x border-dashed border-primary/40 bg-primary-subtle"
            style={{
              left: `${axisPercent(window.start, axis)}%`,
              right: `${100 - axisPercent(window.end, axis)}%`,
            }}
          />
        ) : null}

        <span aria-hidden className="absolute inset-x-0 bottom-0 h-px bg-border" />

        {days.map((day) => {
          const picked = day.entries.filter((entry) =>
            entry.items.every((item) => selected.has(item.id)),
          ).length;

          return (
            <button
              key={day.key}
              type="button"
              disabled={isDisabled}
              aria-pressed={picked === day.entries.length}
              title={`${dayLabel(day.date)} · ${day.entries.map((entry) => entry.versionTag).join(", ")}`}
              className={cn(
                "absolute bottom-0 flex -translate-x-1/2 flex-col-reverse items-center gap-0.5 rounded-sm px-1 pt-2 outline-offset-2",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring",
                isDisabled ? "cursor-not-allowed" : "cursor-pointer hover:bg-muted/60",
              )}
              style={{ left: `${axisPercent(day.date, axis)}%` }}
              onClick={() => onToggleDay(day)}
            >
              <span className="sr-only">
                {t("admin.changelog.compose.timeline.day", {
                  date: dayLabel(day.date),
                  count: day.entries.length,
                })}
              </span>
              {day.entries.slice(0, MAX_BLOCKS_PER_DAY).map((entry, index) => (
                <span
                  key={entry.versionTag}
                  aria-hidden
                  className={cn(
                    "h-2 w-3 rounded-[1px] transition-colors",
                    index < picked ? "bg-primary" : "bg-muted-foreground/45",
                  )}
                />
              ))}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between text-[10px] tabular-nums text-muted-foreground">
        <span>{dayLabel(axis.start)}</span>
        <span>{dayLabel(new Date(axis.end.getTime() - 1))}</span>
      </div>
    </div>
  );
}
